/* image-metadata-engine.js — Timbrica Image Metadata Editor engine.
 *
 * The reader is exifr (vendored, read-only, all formats). The WRITER is piexif
 * for JPEG (splices the EXIF APP1 segment — leaves pixels + ICC + XMP + IPTC
 * byte-identical = truly LOSSLESS, never a <canvas> re-encode) plus hand-rolled
 * segment/chunk surgery for lossless strip and PNG/WebP.
 *
 * Depends on globals: `exifr` (read), `piexif` (JPEG write). Both loaded before.
 * Exposes `window.ImageMeta`. Also CommonJS-exported so the write core can be
 * unit-tested in Node on real files (see tests/scratch).
 *
 * DESIGN NOTE — why not canvas: HTML5 canvas drawImage→toBlob holds decoded
 * pixels only; it silently discards EXIF/IPTC/XMP/ICC AND recompresses. Every
 * mutation here operates on the original encoded bytes. (CLAUDE.md: no silent
 * quality downgrade.)
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.ImageMeta = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* ─── piexif handle: window in browser, require() in Node test harness ─── */
    var _piexif = (typeof window !== 'undefined' && window.piexif) ? window.piexif
                : (typeof piexif !== 'undefined' ? piexif : null);
    function px() {
        if (_piexif) return _piexif;
        if (typeof window !== 'undefined' && window.piexif) { _piexif = window.piexif; return _piexif; }
        throw new Error('piexif not loaded');
    }
    // Allow the Node harness to inject piexif (which it require()s itself).
    function _setPiexif(p) { _piexif = p; }

    /* ─── MetaScrub handle (public/js/metadata-scrub.js) ───────────────────
     * The allowlist engine written for /remove-metadata. We delegate to it for
     * every operation where "remove" means "and everything like it": the full
     * wipe, hidden appended payloads, C2PA, and ALL non-JPEG formats.
     *
     * WHY, measured 2026-08-16 on planted fixtures (tests/scratch/imgmeta-strip-scratch.mjs):
     * the local stripJpeg() below is a BLOCKLIST, so "erase everything" left
     *   • the C2PA signed manifest (APP11 JUMBF),
     *   • the MPF extra full-size embedded JPEG (APP2),
     *   • the ENTIRE Motion-Photo MP4 appended past the EOI marker,
     * and simply threw on heic/avif/tiff/gif — i.e. on the iPhone default, the
     * single most common "take my location out" input. A blocklist only knows
     * yesterday's containers; that is the class of bug metadata-scrub.js exists
     * to end, and it was never carried across to this tool.
     *
     * Surgical single-class removal on JPEG deliberately stays on piexif: it
     * must KEEP every other tag byte-for-byte, which an allowlist cannot do. */
    var _scrub = (typeof window !== 'undefined' && window.MetaScrub) ? window.MetaScrub : null;
    function ms() {
        if (_scrub) return _scrub;
        if (typeof window !== 'undefined' && window.MetaScrub) { _scrub = window.MetaScrub; return _scrub; }
        return null;
    }
    function _setScrub(s) { _scrub = s; }

    /* ════════════════ BINARY HELPERS ════════════════ */
    // Chunked to avoid "Maximum call stack" on String.fromCharCode.apply for big files.
    function ab2bstr(buf) {
        var u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
        var s = '', CH = 0x8000;
        for (var i = 0; i < u8.length; i += CH) {
            s += String.fromCharCode.apply(null, u8.subarray(i, Math.min(i + CH, u8.length)));
        }
        return s;
    }
    function bstr2u8(bstr) {
        var len = bstr.length, u8 = new Uint8Array(len);
        for (var i = 0; i < len; i++) u8[i] = bstr.charCodeAt(i) & 0xff;
        return u8;
    }

    /* ════════════════ FORMAT DETECTION (magic bytes) ════════════════ */
    function detectFormat(u8) {
        if (!u8 || u8.length < 12) return 'unknown';
        var b = u8;
        if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpeg';
        if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'png';
        if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
            b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return 'webp';
        if ((b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2A && b[3] === 0x00) ||
            (b[0] === 0x4D && b[1] === 0x4D && b[2] === 0x00 && b[3] === 0x2A)) return 'tiff';
        if (b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) return 'gif';
        if (b[0] === 0x42 && b[1] === 0x4D) return 'bmp';
        // ISO-BMFF image: 'ftyp' box at offset 4
        if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
            var brand = String.fromCharCode(b[8], b[9], b[10], b[11]);
            if (/avif|avis/i.test(brand)) return 'avif';
            if (/hei|hev|mif1|msf1|heic|heix/i.test(brand)) return 'heic';
            return 'heic';
        }
        return 'unknown';
    }
    /* ════════════════ CAPABILITIES ════════════════
     * "Can we write to this file?" is FOUR different questions, and collapsing
     * them into one `writable` boolean shipped dead ends in both directions:
     *
     *   • WebP was writable:true, so the map offered "drag the pin to set a
     *     location" and Save lit up — then edit() threw write_unsupported:webp.
     *   • PNG accepted a GPS edit, fell through the tEXt keyword map, returned
     *     the ORIGINAL bytes and reported SUCCESS — a silent no-op, which is
     *     worse than the throw.
     *
     *   fields  — full EXIF tags via piexif (artist, dates, camera, orientation…)
     *   text    — descriptive text only (PNG iTXt/tEXt keyword subset)
     *   gps     — coordinates can be written/moved (EXIF GPS IFD ⇒ JPEG only)
     *   strip   — metadata can be REMOVED. MUCH wider than the write lanes: the
     *             allowlist scrubber handles every container it supports, so a
     *             HEIC we cannot edit at all is still perfectly cleanable.
     *
     * `fields` and `text` stay separate because PNG can take a description but
     * not a camera model; one flag for both would re-create the same class of
     * lie a level down. A capability the UI must never infer from another one. */
    var CAPS = {
        jpeg:    { fields: true,  text: true,  gps: true,  strip: true },
        png:     { fields: false, text: true,  gps: false, strip: true },
        webp:    { fields: false, text: false, gps: false, strip: true },
        tiff:    { fields: false, text: false, gps: false, strip: true },
        heic:    { fields: false, text: false, gps: false, strip: true },
        avif:    { fields: false, text: false, gps: false, strip: true },
        gif:     { fields: false, text: false, gps: false, strip: true },
        bmp:     { fields: false, text: false, gps: false, strip: false },
        unknown: { fields: false, text: false, gps: false, strip: false },
    };
    var NO_CAPS = { fields: false, text: false, gps: false, strip: false };
    function capabilities(format) {
        var c = CAPS[format] || NO_CAPS;
        // heic/avif/tiff/gif can ONLY be cleaned through the scrubber. If it is
        // not on the page, say so here rather than offering a button that throws.
        if (c.strip && !ms() && format !== 'jpeg' && format !== 'png' && format !== 'webp') {
            return { fields: c.fields, text: c.text, gps: c.gps, strip: false };
        }
        return c;
    }
    // Short alias kept for callers that ask by format string.
    function caps(fmt) { return capabilities(fmt); }
    // Derived view: callers that only ask "can I edit fields?" still work.
    var WRITABLE = {};
    Object.keys(CAPS).forEach(function (k) { WRITABLE[k] = CAPS[k].fields; });

    /* ════════════════ READ (exifr) + GROUPING ════════════════ */
    // Reads everything exifr can, computes GPS decimals, extracts thumbnail,
    // organizes tags into human groups, and flags an out-of-date embedded thumb.
    async function read(file) {
        var buf = await file.arrayBuffer();
        var u8 = new Uint8Array(buf);
        var format = detectFormat(u8);
        var cap = capabilities(format);
        var out = {
            format: format,
            caps: cap,
            // Flat mirrors of the same four answers. `writable` means "can set
            // fields" (NOT "can do anything"): the strip panel must ask
            // caps.strip, because a HEIC is unwritable and still cleanable.
            writable: cap.fields || cap.text,
            canEditGps: cap.gps,
            canStrip: cap.strip,
            size: file.size,
            name: file.name,
            all: {},          // flat exifr object (raw display)
            gps: null,        // {lat, lon, alt, direction, speed}
            thumbnail: null,  // {blob, url, width, height}
            icc: false,
            warnings: [],
            dims: null,       // {w, h}
            hidden: null,     // {bytes, kind} — payload appended past end-of-image
            c2pa: false,      // signed provenance manifest present
            mpf: false,       // extra full-size images embedded via MPF
            damaged: false,
        };
        if (typeof exifr === 'undefined') { out.warnings.push('exifr_missing'); return out; }
        var parsed = null;
        try {
            parsed = await exifr.parse(file, {
                tiff: true, exif: true, gps: true, xmp: true, iptc: true, icc: true,
                jfif: true, ihdr: true, makerNote: false, userComment: true,
                translateKeys: true, translateValues: true, reviveValues: true, mergeOutput: true,
            });
        } catch (e) { out.warnings.push('parse_failed'); }
        out.all = parsed || {};

        // GPS decimals (robust path)
        // Broken rational tags (0/0 denominators, truncated IFDs, missing
        // N/S / E/W refs) parse to NaN — and NaN passes a `!= null` check, so
        // it used to travel all the way into Leaflet as LatLng(NaN, NaN)
        // (Sentry: "Invalid LatLng object"). Finite check at the parse root;
        // 0,0 ("null island", cameras that zero the tags instead of omitting
        // them) is also treated as "no location". out.gps stays null → the UI
        // shows its existing no-geodata placeholder instead of a map.
        try {
            var g = await exifr.gps(file);
            var glat = g ? Number(g.latitude) : NaN;
            var glon = g ? Number(g.longitude) : NaN;
            if (Number.isFinite(glat) && Number.isFinite(glon) && !(glat === 0 && glon === 0)) {
                out.gps = { lat: glat, lon: glon };
                if (parsed) {
                    // Same defect class: a broken rational in the same GPS IFD
                    // must not surface as "NaN m" / "NaN°" in the fields.
                    if (Number.isFinite(parsed.GPSAltitude)) out.gps.alt = (parsed.GPSAltitudeRef === 1 || parsed.GPSAltitudeRef === '1') ? -Math.abs(parsed.GPSAltitude) : parsed.GPSAltitude;
                    if (Number.isFinite(parsed.GPSImgDirection)) out.gps.direction = parsed.GPSImgDirection;
                    if (Number.isFinite(parsed.GPSSpeed)) out.gps.speed = parsed.GPSSpeed;
                }
            }
        } catch (e) {}

        // Dimensions
        if (parsed) {
            var w = parsed.ImageWidth || parsed.ExifImageWidth || parsed.PixelXDimension;
            var h = parsed.ImageHeight || parsed.ExifImageHeight || parsed.PixelYDimension;
            if (w && h) out.dims = { w: w, h: h };
        }
        out.icc = !!(parsed && (parsed.ColorSpaceData || parsed.ProfileDescription || parsed.ICCProfileName));

        // Embedded thumbnail (JPEG IFD1) + leak heuristic
        if (format === 'jpeg') {
            try {
                var thumb = await exifr.thumbnail(file);
                if (thumb && thumb.byteLength) {
                    var tb = new Blob([thumb], { type: 'image/jpeg' });
                    out.thumbnail = { blob: tb, url: null, bytes: thumb.byteLength };
                }
            } catch (e) {}
        }

        /* ─── Things exifr cannot see ───────────────────────────────────────
         * exifr parses metadata CONTAINERS. It has nothing to say about a whole
         * MP4 glued on after the end-of-image marker, about MPF's extra
         * full-size JPEGs, or about a C2PA manifest — all of which travel with
         * the photo and all of which a "what does this file reveal" panel is
         * exactly the place to name. The scrubber already walks for these, so
         * ask it rather than growing a second parser here. */
        var scrub = ms();
        if (scrub && scrub.inspect) {
            try {
                var rep = scrub.inspect(u8, file.name);
                if (rep) {
                    if (rep.hiddenBytes > 0) out.hidden = { bytes: rep.hiddenBytes, kind: rep.hiddenKind || 'data' };
                    out.c2pa = !!(rep._seen && rep._seen.c2pa);
                    out.mpf = !!(rep._seen && rep._seen.mpf);
                    out.damaged = !!rep.damaged;
                    out.scrubFormat = rep.format;
                }
            } catch (e) { out.warnings.push('inspect_failed'); }
        }
        return out;
    }

    /* ════════════════ WRITE — JPEG via piexif (lossless) ════════════════ */
    // piexif tag map: friendly key → [ifd, tagId, type]. ifd is one of the
    // piexif dict keys: '0th' | 'Exif' | 'GPS'. type drives coercion.
    function fieldMap() {
        var P = px();
        return {
            make:          ['0th',  P.ImageIFD.Make, 'ascii'],
            model:         ['0th',  P.ImageIFD.Model, 'ascii'],
            software:      ['0th',  P.ImageIFD.Software, 'ascii'],
            artist:        ['0th',  P.ImageIFD.Artist, 'ascii'],
            copyright:     ['0th',  P.ImageIFD.Copyright, 'ascii'],
            description:   ['0th',  P.ImageIFD.ImageDescription, 'ascii'],
            orientation:   ['0th',  P.ImageIFD.Orientation, 'short'],
            datetime:      ['0th',  P.ImageIFD.DateTime, 'ascii'],
            dateOriginal:  ['Exif', P.ExifIFD.DateTimeOriginal, 'ascii'],
            dateDigitized: ['Exif', P.ExifIFD.DateTimeDigitized, 'ascii'],
            userComment:   ['Exif', P.ExifIFD.UserComment, 'comment'],
            lensModel:     ['Exif', P.ExifIFD.LensModel, 'ascii'],
        };
    }
    function coerce(type, val) {
        if (type === 'short' || type === 'long') { var n = parseInt(val, 10); return isNaN(n) ? null : n; }
        if (type === 'comment') { return 'ASCII\x00\x00\x00' + String(val); } // UserComment charset prefix
        return String(val); // ascii
    }
    function ensureDict(d) {
        d['0th'] = d['0th'] || {}; d['Exif'] = d['Exif'] || {}; d['GPS'] = d['GPS'] || {};
        d['Interop'] = d['Interop'] || {}; d['1st'] = d['1st'] || {};
        return d;
    }
    function setGpsInDict(d, lat, lon, alt) {
        var P = px();
        d['GPS'] = d['GPS'] || {};
        d['GPS'][P.GPSIFD.GPSLatitudeRef] = lat >= 0 ? 'N' : 'S';
        d['GPS'][P.GPSIFD.GPSLatitude] = P.GPSHelper.degToDmsRational(Math.abs(lat));
        d['GPS'][P.GPSIFD.GPSLongitudeRef] = lon >= 0 ? 'E' : 'W';
        d['GPS'][P.GPSIFD.GPSLongitude] = P.GPSHelper.degToDmsRational(Math.abs(lon));
        if (alt != null && alt !== '' && !isNaN(alt)) {
            d['GPS'][P.GPSIFD.GPSAltitudeRef] = alt < 0 ? 1 : 0;
            d['GPS'][P.GPSIFD.GPSAltitude] = [Math.round(Math.abs(alt) * 100), 100];
        }
    }
    // Delete camera/serial-class identifying tags from a loaded dict.
    function deleteCameraTags(d) {
        var P = px();
        [P.ImageIFD.Make, P.ImageIFD.Model, P.ImageIFD.Software, P.ImageIFD.HostComputer].forEach(function (t) { if (t != null) delete d['0th'][t]; });
        [P.ExifIFD.LensModel, P.ExifIFD.LensMake, P.ExifIFD.LensSerialNumber, P.ExifIFD.BodySerialNumber, P.ExifIFD.CameraOwnerName].forEach(function (t) { if (t != null) delete d['Exif'][t]; });
    }
    function deleteDateTags(d) {
        var P = px();
        if (P.ImageIFD.DateTime != null) delete d['0th'][P.ImageIFD.DateTime];
        [P.ExifIFD.DateTimeOriginal, P.ExifIFD.DateTimeDigitized].forEach(function (t) { if (t != null) delete d['Exif'][t]; });
    }

    // Core JPEG mutation. ops = {
    //   set:{artist,copyright,...}, gps:{lat,lon,alt}, deleteGps:bool,
    //   deleteThumbnail:bool, deleteCamera:bool, deleteDates:bool }
    async function writeJpeg(file, ops) {
        var P = px();
        var buf = await file.arrayBuffer();
        var bstr = ab2bstr(new Uint8Array(buf));
        var d;
        try { d = P.load(bstr); } catch (e) { d = {}; }
        d = ensureDict(d);
        var fm = fieldMap();
        ops = ops || {};
        if (ops.set) {
            Object.keys(ops.set).forEach(function (k) {
                var spec = fm[k]; if (!spec) return;
                var v = ops.set[k];
                if (v === '' || v == null) { delete d[spec[0]][spec[1]]; return; } // empty clears
                var cv = coerce(spec[2], v);
                if (cv != null) d[spec[0]][spec[1]] = cv;
            });
        }
        if (ops.deleteCamera) deleteCameraTags(d);
        if (ops.deleteDates) deleteDateTags(d);
        if (ops.deleteThumbnail) { d['1st'] = {}; d['thumbnail'] = null; }
        if (ops.deleteGps) d['GPS'] = {};
        if (ops.gps) setGpsInDict(d, ops.gps.lat, ops.gps.lon, ops.gps.alt);
        var exifBytes = P.dump(d);
        var newBstr = P.insert(exifBytes, bstr);
        return new Blob([bstr2u8(newBstr)], { type: 'image/jpeg' });
    }

    /* ─── JPEG lossless strip (segment surgery — pixels copied verbatim) ─── */
    // opts: which segment classes to REMOVE. Defaults strip personal/descriptive
    // metadata but KEEP ICC (color fidelity) unless icc:true.
    function stripJpeg(u8, opts) {
        opts = opts || {};
        var rm = {
            exif: opts.exif !== false, xmp: opts.xmp !== false, iptc: opts.iptc !== false,
            comment: opts.comment !== false, icc: !!opts.icc, app: !!opts.app,
        };
        var keep = []; // [start,end) ranges to retain
        var n = u8.length, i = 0;
        keep.push([0, 2]); // SOI
        i = 2;
        function asc(off, len) { var s = ''; for (var k = 0; k < len && off + k < n; k++) s += String.fromCharCode(u8[off + k]); return s; }
        while (i < n - 1) {
            if (u8[i] !== 0xFF) { keep.push([i, n]); break; }
            var marker = u8[i + 1];
            if (marker === 0xD9) { keep.push([i, n]); break; }                 // EOI
            if (marker === 0xDA) { keep.push([i, n]); break; }                 // SOS → copy scan+rest verbatim
            if (marker >= 0xD0 && marker <= 0xD7) { keep.push([i, i + 2]); i += 2; continue; } // RSTn
            if (marker === 0x01) { keep.push([i, i + 2]); i += 2; continue; }  // TEM
            if (i + 3 >= n) { keep.push([i, n]); break; }
            var len = (u8[i + 2] << 8) | u8[i + 3];
            var segEnd = i + 2 + len;
            if (segEnd > n) { keep.push([i, n]); break; }
            var drop = false;
            if (marker === 0xE1) {                       // APP1: EXIF or XMP
                if (asc(i + 4, 4) === 'Exif' && rm.exif) drop = true;
                else if (asc(i + 4, 28).indexOf('http://ns.adobe.com/xap') === 0 && rm.xmp) drop = true;
            } else if (marker === 0xED) { if (rm.iptc) drop = true; }        // APP13 Photoshop/IPTC
            else if (marker === 0xFE) { if (rm.comment) drop = true; }       // COM
            else if (marker === 0xE2) {                  // APP2 ICC / MPF
                if (asc(i + 4, 11) === 'ICC_PROFILE' && rm.icc) drop = true;
            } else if (marker >= 0xE3 && marker <= 0xEF) { if (rm.app) drop = true; } // other APPn
            if (!drop) keep.push([i, segEnd]);
            i = segEnd;
        }
        var total = 0, r; for (r = 0; r < keep.length; r++) total += keep[r][1] - keep[r][0];
        var out = new Uint8Array(total), pos = 0;
        for (r = 0; r < keep.length; r++) { out.set(u8.subarray(keep[r][0], keep[r][1]), pos); pos += keep[r][1] - keep[r][0]; }
        return out;
    }

    /* ════════════════ PNG chunk surgery ════════════════ */
    var CRC_TABLE = (function () {
        var t = new Uint32Array(256);
        for (var n = 0; n < 256; n++) { var c = n; for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1); t[n] = c >>> 0; }
        return t;
    })();
    function crc32(u8, start, end) {
        var c = 0xFFFFFFFF;
        for (var i = start; i < end; i++) c = CRC_TABLE[(c ^ u8[i]) & 0xFF] ^ (c >>> 8);
        return (c ^ 0xFFFFFFFF) >>> 0;
    }
    var PNG_SIG = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
    function walkPng(u8) {
        var chunks = [], i = 8; // skip signature
        while (i + 8 <= u8.length) {
            var len = (u8[i] << 24 | u8[i + 1] << 16 | u8[i + 2] << 8 | u8[i + 3]) >>> 0;
            var type = String.fromCharCode(u8[i + 4], u8[i + 5], u8[i + 6], u8[i + 7]);
            var end = i + 12 + len;
            if (end > u8.length) break;
            chunks.push({ type: type, start: i, end: end, dataStart: i + 8, dataLen: len });
            i = end;
            if (type === 'IEND') break;
        }
        return chunks;
    }
    // Strip PNG metadata chunks losslessly. opts.text/exif/time booleans (default all true).
    function stripPng(u8, opts) {
        opts = opts || {};
        var META = { tEXt: 'text', zTXt: 'text', iTXt: 'text', eXIf: 'exif', tIME: 'time' };
        var rmClass = { text: opts.text !== false && opts.xmp !== false, exif: opts.exif !== false, time: opts.comment !== false };
        var chunks = walkPng(u8);
        var keep = [[0, 8]];
        chunks.forEach(function (c) {
            var cls = META[c.type];
            if (cls && rmClass[cls]) return; // drop
            keep.push([c.start, c.end]);
        });
        var total = 0, r; for (r = 0; r < keep.length; r++) total += keep[r][1] - keep[r][0];
        var out = new Uint8Array(total), pos = 0;
        for (r = 0; r < keep.length; r++) { out.set(u8.subarray(keep[r][0], keep[r][1]), pos); pos += keep[r][1] - keep[r][0]; }
        return out;
    }
    // Write a PNG text chunk (tEXt for latin1 keyword=value; iTXt for UTF-8).
    function makePngChunk(type, data) {
        var out = new Uint8Array(12 + data.length);
        var dv = new DataView(out.buffer);
        dv.setUint32(0, data.length);
        out[4] = type.charCodeAt(0); out[5] = type.charCodeAt(1); out[6] = type.charCodeAt(2); out[7] = type.charCodeAt(3);
        out.set(data, 8);
        dv.setUint32(8 + data.length, crc32(out, 4, 8 + data.length));
        return out;
    }
    function utf8(s) {
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s);
        var u = unescape(encodeURIComponent(s)), a = new Uint8Array(u.length);
        for (var i = 0; i < u.length; i++) a[i] = u.charCodeAt(i); return a;
    }
    // Set/replace text metadata pairs {keyword: value} in a PNG (iTXt, UTF-8).
    function writePngText(u8, pairs) {
        var chunks = walkPng(u8);
        var iend = null;
        for (var c = 0; c < chunks.length; c++) if (chunks[c].type === 'IEND') { iend = chunks[c]; break; }
        if (!iend) return u8;
        var keys = Object.keys(pairs);
        // Remove existing iTXt/tEXt chunks whose keyword collides, then append new.
        var collide = {}; keys.forEach(function (k) { collide[k] = 1; });
        var keep = [[0, 8]];
        chunks.forEach(function (ch) {
            if (ch.type === 'IEND') return;
            if (ch.type === 'tEXt' || ch.type === 'iTXt') {
                // keyword is the ascii prefix up to a NUL
                var kw = ''; for (var p = ch.dataStart; p < ch.dataStart + ch.dataLen && u8[p] !== 0; p++) kw += String.fromCharCode(u8[p]);
                if (collide[kw]) return; // drop, we'll re-add
            }
            keep.push([ch.start, ch.end]);
        });
        var newChunks = [];
        keys.forEach(function (k) {
            var v = pairs[k];
            if (v == null || v === '') return;
            // iTXt: keyword\0 compFlag(0) compMethod(0) langTag\0 transKeyword\0 text(UTF-8)
            var kwB = utf8(k), txtB = utf8(String(v));
            var data = new Uint8Array(kwB.length + 5 + txtB.length);
            var o = 0; data.set(kwB, o); o += kwB.length; data[o++] = 0; data[o++] = 0; data[o++] = 0; data[o++] = 0; data[o++] = 0;
            data.set(txtB, o);
            newChunks.push(makePngChunk('iTXt', data));
        });
        var total = 0, r;
        for (r = 0; r < keep.length; r++) total += keep[r][1] - keep[r][0];
        for (r = 0; r < newChunks.length; r++) total += newChunks[r].length;
        total += iend.end - iend.start;
        var out = new Uint8Array(total), pos = 0;
        for (r = 0; r < keep.length; r++) { out.set(u8.subarray(keep[r][0], keep[r][1]), pos); pos += keep[r][1] - keep[r][0]; }
        for (r = 0; r < newChunks.length; r++) { out.set(newChunks[r], pos); pos += newChunks[r].length; }
        out.set(u8.subarray(iend.start, iend.end), pos);
        return out;
    }

    /* ════════════════ WebP RIFF chunk surgery (strip) ════════════════ */
    function stripWebp(u8, opts) {
        opts = opts || {};
        if (u8.length < 12) return u8;
        // RIFF size at 4..8 (LE) covers everything after byte 8.
        var i = 12; // after 'RIFF'<size>'WEBP'
        var keep = [[0, 12]];
        var vp8xIdx = -1;
        while (i + 8 <= u8.length) {
            var fourcc = String.fromCharCode(u8[i], u8[i + 1], u8[i + 2], u8[i + 3]);
            var size = (u8[i + 4] | (u8[i + 5] << 8) | (u8[i + 6] << 16) | (u8[i + 7] << 24)) >>> 0;
            var padded = size + (size & 1);
            var end = i + 8 + padded;
            if (end > u8.length) { keep.push([i, u8.length]); i = u8.length; break; }
            var dropIt = (fourcc === 'EXIF' && opts.exif !== false) || (fourcc === 'XMP ' && opts.xmp !== false);
            if (fourcc === 'VP8X') vp8xIdx = keep.length; // remember to clear flags
            if (!dropIt) keep.push([i, end]);
            i = end;
        }
        var total = 0, r; for (r = 0; r < keep.length; r++) total += keep[r][1] - keep[r][0];
        var out = new Uint8Array(total), pos = 0, ranges = [];
        for (r = 0; r < keep.length; r++) { var s = keep[r][0], e = keep[r][1]; out.set(u8.subarray(s, e), pos); ranges.push(pos); pos += e - s; }
        // Fix RIFF chunk size (bytes 4..8 = fileSize - 8).
        var dv = new DataView(out.buffer);
        dv.setUint32(4, out.length - 8, true);
        // Clear EXIF(bit3)/XMP(bit2) flags in VP8X so readers don't expect the chunks.
        if (vp8xIdx > 0) {
            var vp8xPos = ranges[vp8xIdx]; // start of VP8X chunk in out
            var flagsByte = vp8xPos + 8;   // first byte of VP8X payload holds the reserved+flags
            if (opts.exif !== false) out[flagsByte] &= ~0x08;
            if (opts.xmp !== false) out[flagsByte] &= ~0x04;
        }
        return out;
    }

    /* ════════════════ TOP-LEVEL FILE OPS (Blob in → Blob out) ════════════════ */
    function ext(name) { var m = /\.([a-z0-9]+)$/i.exec(name || ''); return m ? m[1].toLowerCase() : ''; }
    function outName(name, suffix) { return (name || 'image').replace(/\.[^.]+$/, '') + suffix + '.' + (ext(name) || 'jpg'); }

    // Edit fields / GPS on a JPEG (lossless). Returns Blob.
    async function edit(file, ops) {
        var fmt = detectFormat(new Uint8Array(await peek(file, 16)));
        ops = ops || {};
        /* A coordinate edit this format cannot carry must FAIL, not pass. PNG
           holds only tEXt keywords, so a GPS edit used to fall through the
           `pairs` map, produce an unchanged buffer and report success — the
           caller downloaded a byte-identical file and was told it was saved.
           Refusing is the honest answer; the UI no longer offers it at all. */
        var wantsGps = ops.gps !== undefined || !!ops.deleteGps;
        if (wantsGps && !caps(fmt).gps) throw new Error('gps_unsupported:' + fmt);

        if (fmt === 'jpeg') return writeJpeg(file, ops);
        if (fmt === 'png') {
            // PNG: only text metadata (mapped to standard keywords) is writable.
            var u8 = new Uint8Array(await file.arrayBuffer());
            var pairs = mapEditsToPngKeywords(ops);
            var res = Object.keys(pairs).length ? writePngText(u8, pairs) : u8;
            return new Blob([res], { type: 'image/png' });
        }
        throw new Error('write_unsupported:' + fmt);
    }
    function mapEditsToPngKeywords(ops) {
        var p = {}, s = (ops && ops.set) || {};
        if (s.artist != null) p['Author'] = s.artist;
        if (s.copyright != null) p['Copyright'] = s.copyright;
        if (s.description != null) p['Description'] = s.description;
        if (s.software != null) p['Software'] = s.software;
        if (s.title != null) p['Title'] = s.title;
        if (s.comment != null) p['Comment'] = s.comment;
        return p;
    }

    var MIME = { jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif',
                 heic: 'image/heic', avif: 'image/avif', tiff: 'image/tiff' };

    /* Strip metadata (lossless) across formats.
     * opts = {gps, camera, dates, thumb, hidden, c2pa, all, icc}
     *
     * TWO LANES, chosen by what "remove" has to mean here:
     *
     *  • ALLOWLIST (metadata-scrub.js) — the full wipe, anything touching hidden
     *    appended payloads / C2PA, and every non-JPEG container. "Remove
     *    everything" must also remove the containers nobody has invented yet,
     *    which only an allowlist does. It also covers heic/avif/tiff/gif, where
     *    this file previously just threw.
     *
     *  • SURGICAL (piexif) — single-class removal on JPEG. Here the requirement
     *    is the opposite: keep every other tag exactly as it was, which an
     *    allowlist cannot promise because it rebuilds the block.
     *
     * Returns a Blob; also carries `.report` when the allowlist lane ran, so the
     * UI can say what actually went rather than guessing. */
    async function strip(file, opts) {
        opts = opts || {};
        var u8 = new Uint8Array(await file.arrayBuffer());
        var fmt = detectFormat(u8);
        var scrub = ms();

        var wantsAllowlist = !!(opts.all || opts.hidden || opts.c2pa) || fmt !== 'jpeg';

        if (wantsAllowlist) {
            if (!scrub) {
                // No scrubber on the page. Rather than silently falling back to
                // the blocklist that misses C2PA/Motion Photos, say so: a strip
                // that quietly under-delivers is worse than one that refuses.
                if (fmt === 'jpeg' || fmt === 'png' || fmt === 'webp') throw new Error('scrub_unavailable');
                throw new Error('strip_unsupported:' + fmt);
            }
            var keep;
            if (opts.all) {
                keep = scrub.presetKeep('full');
                if (opts.icc) keep.icc = false;   // only when explicitly asked
            } else {
                // Keep everything, then drop exactly what was ticked.
                keep = {
                    gps: !opts.gps, datetime: !opts.dates,
                    camera: !opts.camera, serial: !opts.camera,
                    settings: true, person: true, copyright: true,
                    thumbnail: !opts.thumb,
                    c2pa: !opts.c2pa, hidden: !opts.hidden,
                    icc: !opts.icc, other: true,
                };
            }
            var r = scrub.scrub(u8, { keep: keep, keepOrientation: true, name: file.name });
            if (r.report && r.report.warnings && r.report.warnings.indexOf('unsupported') >= 0) {
                throw new Error('strip_unsupported:' + fmt);
            }
            var blob = new Blob([r.bytes], { type: MIME[fmt] || file.type || 'application/octet-stream' });
            try { blob.report = r.report; } catch (e) {}
            return blob;
        }

        // Surgical JPEG lane — keeps every untouched tag byte-identical.
        return writeJpeg(file, { deleteGps: !!opts.gps, deleteCamera: !!opts.camera, deleteDates: !!opts.dates });
    }

    async function peek(file, n) { return (file.slice ? file.slice(0, n) : file).arrayBuffer(); }

    /* ════════════════ EXPORTS ════════════════ */
    return {
        // read
        read: read, detectFormat: detectFormat, WRITABLE: WRITABLE,
        CAPS: CAPS, capabilities: capabilities, caps: caps,
        // high-level ops
        edit: edit, strip: strip,
        // low-level (for tests / advanced)
        writeJpeg: writeJpeg, stripJpeg: stripJpeg,
        walkPng: walkPng, stripPng: stripPng, writePngText: writePngText,
        stripWebp: stripWebp, setGpsInDict: setGpsInDict, fieldMap: fieldMap,
        ab2bstr: ab2bstr, bstr2u8: bstr2u8, outName: outName, ext: ext,
        _setPiexif: _setPiexif, _setScrub: _setScrub,
    };
});
